import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check } from 'lucide-react';
import type { RGPDConfig } from '../../types/rgpd';
import type { SupportedLanguages } from '../../i18n';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';
import { analyticsService } from '../../lib/analytics/analyticsService';

interface CookiePreferencesProps {
  config: RGPDConfig[];
  appConfig: any;
  onClose: () => void;
  onSave: (preferences: { analytics: boolean }) => void;
}

export const CookiePreferences: React.FC<CookiePreferencesProps> = ({
  config,
  appConfig,
  onClose,
  onSave
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguages;
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  // Récupérer les traductions depuis trad_txt
  const { translations } = useTranslatedContent({
    terms: ['cookie_preferences', 'cookie_required', 'cookie_save']
  });

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[currentLanguage] || translation.fr || key;
  };

  const getConfigText = (key: string): string => {
    const item = config.find(c => c.key === key);
    if (!item) return key;

    const langKey = `title_${currentLanguage}` as keyof RGPDConfig;
    return (item[langKey] as string) || item.title_fr;
  };

  const getConfigDescription = (key: string): string => {
    const item = config.find(c => c.key === key);
    if (!item) return key;

    const langKey = `description_${currentLanguage}` as keyof RGPDConfig;
    return (item[langKey] as string) || item.description_fr;
  };

  const handleSave = () => {
    // Track preferences before saving
    analyticsService.trackEvent({
      event_type: 'user',
      event_name: 'cookie_preferences_save',
      event_data: {
        analytics: analyticsEnabled,
        timestamp: new Date().toISOString()
      }
    });
    
    onSave({ analytics: analyticsEnabled });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleToggleAnalytics = () => {
    setAnalyticsEnabled(!analyticsEnabled);
    
    // Track toggle action
    analyticsService.trackEvent({
      event_type: 'interaction',
      event_name: 'cookie_preference_toggle',
      event_data: {
        preference: 'analytics',
        value: !analyticsEnabled,
        timestamp: new Date().toISOString()
      }
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
        style={{ borderRadius: appConfig.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10">
          <div className="flex justify-between items-center p-4">
            <h2 
              className="text-lg font-semibold"
              style={{ color: appConfig['dark-color'] }}
            >
              {getTranslatedText('cookie_preferences')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Essential Cookies */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{getConfigText('cookie_essential')}</h3>
              <div 
                className="px-2 py-0.5 text-xs rounded-full text-white"
                style={{ backgroundColor: appConfig['medium-color'] }}
              >
                {getTranslatedText('cookie_required')}
              </div>
            </div>
            <p className="text-sm text-gray-600">
              {getConfigDescription('cookie_essential')}
            </p>
          </div>

          {/* Analytics Cookies */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{getConfigText('cookie_analytics')}</h3>
              <button
                onClick={handleToggleAnalytics}
                className={`w-10 h-5 rounded-full transition-colors relative ${
                  analyticsEnabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
                aria-pressed={analyticsEnabled}
                role="switch"
              >
                <span 
                  className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    analyticsEnabled ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {getConfigDescription('cookie_analytics')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <button
            onClick={handleSave}
            className="w-full px-4 py-2 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
            style={{ 
              backgroundColor: appConfig['major-color'],
              borderRadius: appConfig.border_radius 
            }}
          >
            <Check size={18} />
            <span>{getTranslatedText('cookie_save')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};